import { useState } from 'react';
import { Link } from 'react-router-dom';

import { isEmpty } from 'lodash';

import {
  StudentsDataListFilterProps,
  StudentsDataListResponse
} from 'entities/Dataset/sdk';

import CircularProgress from 'components/CircularProgress';
import Paper from 'components/Paper';
import Table from 'components/Table';
import TableBody from 'components/TableBody';
import TableCell from 'components/TableCell';
import TableContainer from 'components/TableContainer';
import TableFooter from 'components/TableFooter';
import TableHead from 'components/TableHead';
import TablePagination from 'components/TablePagination';
import TableRow from 'components/TableRow';

interface StudentsDataListTableProps {
  studentsDataList: StudentsDataListResponse | undefined;
  isLoadingStudentsDataList: boolean;
  filters: StudentsDataListFilterProps;
  onFilter: (filters: StudentsDataListFilterProps) => void;
}

const StudentsDataListTable = ({
  studentsDataList,
  isLoadingStudentsDataList,
  filters,
  onFilter
}: StudentsDataListTableProps) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(filters.limit);

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);

    onFilter({
      gender: filters.gender,
      grade: filters.grade,
      template_grade: filters.template_grade,
      ethnicity: filters.ethnicity,
      state: filters.state,
      is_low_income: filters.is_low_income,
      is_on_individualized_education_plan:
        filters.is_on_individualized_education_plan,
      assignment_name: filters.assignment_name,
      standard_code: filters.standard_code,
      english_learner_status: filters.english_learner_status,
      limit: filters.limit,
      offset: newPage * filters.limit
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);

    onFilter({
      gender: filters.gender,
      grade: filters.grade,
      template_grade: filters.template_grade,
      ethnicity: filters.ethnicity,
      state: filters.state,
      is_low_income: filters.is_low_income,
      is_on_individualized_education_plan:
        filters.is_on_individualized_education_plan,
      assignment_name: filters.assignment_name,
      standard_code: filters.standard_code,
      english_learner_status: filters.english_learner_status,
      limit: newLimit,
      offset: filters.offset
    });

    setPage(0);
  };

  return (
    <TableContainer component={Paper}>
      <div className="w-full overflow-x-auto">
        <Table className="w-full min-w-[1200px] text-sm">
          <TableHead>
            <TableRow className="bg-neutral-200">
              <TableCell>Student ID</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Student Grade</TableCell>
              <TableCell>Gender</TableCell>
              <TableCell>Ethnicity</TableCell>

              <TableCell>Low Income</TableCell>
              <TableCell>IEP</TableCell>
              <TableCell>Assignment</TableCell>
              <TableCell>Standards</TableCell>
              <TableCell>LEP</TableCell>

              <TableCell>Student Work</TableCell>
              <TableCell>Overall response</TableCell>
              <TableCell>Misconceptions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoadingStudentsDataList && (
              <TableRow>
                <TableCell colSpan={13}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}

            {!isLoadingStudentsDataList &&
              !isEmpty(studentsDataList?.results) && (
                <>
                  {studentsDataList?.results.map((row, index) => (
                    <TableRow
                      key={index}
                      className={index % 2 == 0 ? 'bg-white' : 'bg-neutral-100'}
                    >
                      <TableCell>{row.research_portal_id}</TableCell>
                      <TableCell>{row.enrollment_data.state || '-'}</TableCell>
                      <TableCell>{row.enrollment_data.grade}</TableCell>
                      <TableCell>{row.enrollment_data.gender || '-'}</TableCell>
                      <TableCell>
                        {row.enrollment_data.ethnicity || '-'}
                      </TableCell>

                      <TableCell>
                        {row.enrollment_data.is_low_income || '-'}
                      </TableCell>
                      <TableCell>
                        {row.enrollment_data
                          .is_on_individualized_education_plan || '-'}
                      </TableCell>
                      <TableCell>
                        {row.assignment_data.assignment_name}
                      </TableCell>
                      <TableCell>
                        {row.assignment_data.standards_list.join(', ')}
                      </TableCell>
                      <TableCell>
                        {row.enrollment_data.english_learner_status || '-'}
                      </TableCell>

                      <TableCell>
                        <Link
                          to={row.file_url}
                          target="_blank"
                          className="text-blue-600 underline hover:text-blue-800"
                        >
                          link
                        </Link>
                      </TableCell>
                      <TableCell>
                        {row.overall_response_data.description}
                      </TableCell>
                      <TableCell>
                        {row.misconceptions_data
                          .map((msc) => {
                            return msc.name;
                          })
                          .join(', ')}
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              )}

            {!isLoadingStudentsDataList &&
              isEmpty(studentsDataList?.results) && (
                <TableRow>
                  <TableCell colSpan={13}>No data found.</TableCell>
                </TableRow>
              )}
          </TableBody>

          <TableFooter>
            <TableRow>
              <TablePagination
                count={studentsDataList?.count || 0}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                disabled={isLoadingStudentsDataList}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </TableContainer>
  );
};

export default StudentsDataListTable;
